import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMiniERPEDI } from './hooks/useMiniERPEDI';
import MiniERPTrasCaseDetail from './MiniERPTrasCaseDetail';
import MiniERPTrasPromiseToPay from './MiniERPTrasPromiseToPay';
import MiniERPTrasCostTime from './MiniERPTrasCostTime';
import MiniERPTrasRecoveryRoi from './MiniERPTrasRecoveryRoi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faListUl, faUserClock, faHandshake, faClock, faChartPie,
} from '@fortawesome/free-solid-svg-icons';

const NAV_ITEMS = [
  { key: 'detail', label: 'Case', icon: faUserClock },
  { key: 'ptp', label: 'PTP', icon: faHandshake },
  { key: 'costtime', label: 'Cost/Time', icon: faClock },
  { key: 'roi', label: 'ROI', icon: faChartPie },
];

// Bottom-nav SPA structure matching the original kit — one mounted route
// (/minierp/tras) holding "current case" state, switching between screens
// instead of separate routed pages, so both entry points land in the same
// place: the Dashboard queue, or straight into a specific case's detail.
const MiniERPTrasDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchTrasCases, getOrCreateTrasCase } = useMiniERPEDI();

  const [screen, setScreen] = useState('list'); // 'list' | 'detail' | 'ptp' | 'costtime' | 'roi'
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadList = async () => {
    setLoading(true);
    const res = await fetchTrasCases();
    setCases(res?.data?.cases || []);
    setStats(res?.data?.stats || {});
    setLoading(false);
  };

  useEffect(() => {
    // Entry point 2: opened from the Sales Register with a specific
    // transaction — create-if-missing, jump straight to Case Detail.
    const openTxId = searchParams.get('open');
    if (openTxId) {
      getOrCreateTrasCase(openTxId).then((res) => {
        setSelectedCaseId(res?.data?._id);
        setScreen('detail');
      });
    } else {
      loadList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCase = (caseId) => {
    setSelectedCaseId(caseId);
    setScreen('detail');
  };

  const backToList = () => {
    setScreen('list');
    setSelectedCaseId(null);
    loadList();
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 pb-20">
      <div className="max-w-lg mx-auto p-4">
        <div className="flex items-center gap-3 mb-4">
          {screen === 'list' ? (
            <button onClick={() => navigate('/minierp/home')} className="w-fit px-2 py-1 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700">
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Home
            </button>
          ) : (
            <button onClick={backToList} className="w-fit px-2 py-1 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700">
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Cases
            </button>
          )}
          <h1 className="text-xl font-bold text-cyan-200">TraS — Trade to Settlement</h1>
        </div>

        {screen === 'list' && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="bg-gray-800 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-rose-400">₹{stats.totalOutstanding || 0}</div>
                <div className="text-gray-500 text-xs">Total Outstanding</div>
              </div>
              <div className="bg-gray-800 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-amber-400">{stats.openCases || 0}</div>
                <div className="text-gray-500 text-xs">Open Cases</div>
              </div>
              <div className="bg-gray-800 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-emerald-400">{stats.fullyCollected || 0}</div>
                <div className="text-gray-500 text-xs">Fully Collected</div>
              </div>
              <div className="bg-gray-800 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-gray-400">{stats.totalCases || 0}</div>
                <div className="text-gray-500 text-xs">Total Cases</div>
              </div>
            </div>

            <h2 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
              <FontAwesomeIcon icon={faListUl} /> Open Cases
            </h2>
            {loading && <p className="text-gray-500 text-sm">Loading...</p>}
            <div className="space-y-2">
              {!loading && cases.length === 0 && (
                <p className="text-cyan-200 text-sm py-8 text-center">
                  No TraS cases yet. Cases appear here automatically once a sale's due date passes with a balance still outstanding — or open one manually from the Sales Register.
                </p>
              )}
              {cases.map((c) => (
                <button key={c._id} onClick={() => openCase(c._id)}
                  className="w-full text-left bg-gray-800 border border-gray-700 hover:border-cyan-500/40 rounded-xl p-3 text-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-white font-medium">{c.invoice?.customerName || 'Walk-in'}</div>
                      <div className="text-gray-500 text-xs font-mono">{c.caseNumber}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-rose-400 font-bold">₹{c.invoice?.outstandingAmount || 0}</div>
                      <div className="text-gray-500 text-xs">{c.status}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {screen === 'detail' && <MiniERPTrasCaseDetail caseId={selectedCaseId} />}
        {screen === 'ptp' && <MiniERPTrasPromiseToPay caseId={selectedCaseId} />}
        {screen === 'costtime' && <MiniERPTrasCostTime caseId={selectedCaseId} />}
        {screen === 'roi' && <MiniERPTrasRecoveryRoi caseId={selectedCaseId} />}
      </div>

      {/* Bottom nav — only shown once a case is open */}
      {selectedCaseId && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 flex justify-around py-2 max-w-lg mx-auto">
          {NAV_ITEMS.map((item) => (
            <button key={item.key} onClick={() => setScreen(item.key)}
              className={`flex flex-col items-center gap-1 text-xs w-fit px-3 py-1 ${screen === item.key ? 'text-cyan-400' : 'text-gray-500'}`}>
              <FontAwesomeIcon icon={item.icon} />
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MiniERPTrasDashboard;
